import React, { useState } from 'react';
import styled from 'styled-components';
import { Grid, Row, Item } from 'features/shared/Grid';
import { Button } from './DownloadButton';

import DoughnutChart from 'features/shared/ui/DoughnutChart';
import Tooltip, { TooltipPlacement } from 'features/shared/Tooltip';
import { Warning } from 'features/shared/ui/sharedStyled';
import { TotalStatistic } from '../model';
import pluralize from 'utils/pluralize';
import { numberWithSpaces } from 'utils/numberWithSpaces';
import GeneralReportModal from './GeneralReportModal';
import { LogClick, useClickStreamContext } from 'features/shared/ClickStream';
import { SubHeader } from 'features/shared/ui/Page';
import { Space } from '@gr-monorepo/common/ui';

const Title = styled.div`
  font-weight: bold;
  font-size: 24px;
  line-height: 33px;
  margin: 24px 0 34px;
  display: flex;
  align-items: center;
`;
const Numbers = styled.div`
  font-weight: bold;
  font-size: 30px;
  line-height: 40px;
  margin-bottom: 8px;
  background: #f4f5f6;
  border-radius: 6px;
  padding: 4px 9px;
  display: inline-block;
  min-width: 48px;
  text-align: center;
`;

const Text = styled.div`
  font-size: 16px;
  line-height: 22px;
  color: rgba(9, 21, 38, 0.6);
`;

const TextVerticalCenter = styled(Text)`
  height: 100%;
  align-items: center;
  display: flex;
`;

const GeneralStatistics = ({
  totalStatistic,
  educationYear,
  selectedCurrentYear,
}: {
  totalStatistic?: TotalStatistic | null;
  educationYear?: string;
  selectedCurrentYear?: boolean;
}) => {
  const [openModal, setOpenModal] = useState(false);
  const { logPageEvent } = useClickStreamContext();
  const activeStudents = (
    <Item size={3} style={{ maxWidth: 263 }}>
      <Numbers>{numberWithSpaces(totalStatistic?.activeStudentsCount || 0)}</Numbers>
      <Text>
        {pluralize(totalStatistic?.activeStudentsCount || 0, [
          'ученик занимается',
          'ученика занимаются',
          'учеников занимаются',
        ])}{' '}
        на&nbsp;Учи.ру
        <br />в этом учебном году
      </Text>
    </Item>
  );
  return (
    <Grid>
      <Row>
        <Item size={9}>
          <SubHeader>
            Общая статистика <Space />
          </SubHeader>
        </Item>
      </Row>
      {totalStatistic ? (
        <>
          <Row>
            {selectedCurrentYear ? (
              <>
                <Item size={3}>
                  <Numbers>{totalStatistic?.planned}</Numbers>
                  <Text>
                    {pluralize(totalStatistic?.planned || 0, [
                      'олимпиада запланирована',
                      'олимпиады запланировано',
                      'олимпиад запланировано',
                    ])}{' '}
                    1 сентября — 31 августа
                  </Text>
                </Item>
                <Item size={3}>
                  <div>
                    <Numbers> {totalStatistic?.remaining} </Numbers>
                    <Tooltip
                      title="Количество может измениться"
                      arrow
                      placement={TooltipPlacement.TopStart}
                      width={276}
                      onShow={() => {
                        logPageEvent?.({
                          eventType: 'olymp_show_tooltip_remaining_olympiads',
                          eventInfo: 'Олимпиада: Показ тултипа "Оставшиеся олимпиады"',
                        });
                      }}
                    >
                      <Warning style={{ marginBottom: -3, marginLeft: 4 }} />
                    </Tooltip>
                  </div>
                  <Text>
                    {pluralize(totalStatistic?.remaining || 0, [
                      'олимпиада осталась',
                      'олимпиады осталось',
                      'олимпиад осталось',
                    ])}{' '}
                    <br /> до 31 августа
                  </Text>
                </Item>
                <Item size={3}>
                  <Numbers>{totalStatistic?.current}</Numbers>
                  <Text>
                    {pluralize(totalStatistic?.current || 0, [
                      'олимпиада доступна',
                      'олимпиады доступно',
                      'олимпиад доступно',
                    ])}{' '}
                    для участия сейчас
                  </Text>
                </Item>
              </>
            ) : (
              <>
                <Item size={3}>
                  <Numbers>{totalStatistic?.planned}</Numbers>
                  <Text>
                    {pluralize(totalStatistic?.planned || 0, [
                      'олимпиада проводилась',
                      'олимпиады проводились',
                      'олимпиад проводилось',
                    ])}{' '}
                    в&nbsp;{educationYear} учебном году
                  </Text>
                </Item>
              </>
            )}
          </Row>
        </>
      ) : (
        <>
          <Space axis="vertical" size={40} />
          Произошла ошибка при загрузке статистики
          <Space axis="vertical" size={40} />
        </>
      )}
    </Grid>
  );
};

export default GeneralStatistics;
